import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { trackGameClick as trackGameClickEvent, insertTrackEvent } from '@comeon/analytics';
import Card from '@comeon/ui/src/cards/base/card';
import CardMedia from '@comeon/ui/src/cards/base/card-media';

import { selectCurrency, selectLocale } from '~/components/account/ducks/index';
import { startGamePlay } from '~/components/account/reality-check/ducks';
import {
	selectEnableLiveCasino3,
	selectLicense,
	selectShowDropsWinsPragmatic
} from '~/components/brand/ducks/selectors';
import { GAME_MODE } from '~/components/casino-shared/constants';
import {
	gameDetailByGameCodeSelector,
	gameJackpotDataSelector,
	hasDepositLoginSelector,
	jackpotsSelector
} from '~/components/casino-shared/ducks/selectors';
import { useCasinoSharedDispatch, useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';
import useGameLauncher from '~/components/casino-shared/explore/utilities/use-game-launcher';
import useToggleGameInfo from '~/components/casino-shared/explore/utilities/use-toggle-game-info';
import type { EnhancedGame } from '~/components/casino-shared/types';
import { EnhancedAndFormattedImageView } from '~/components/casino-shared/utilities/enhanceImage';
import getNormalizedGameType from '~/components/casino-shared/utilities/get-normalized-game-type';
import useIngameImage from '~/components/casino-shared/utilities/use-ingame-image';
import useIsFunModeAllowed from '~/components/casino-shared/utilities/use-is-fun-mode-allowed';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { gameLiveCasinoDataSelector, useLiveCasinoSelector } from '~/components/live-casino/ducks/selectors';
import { showNotification } from '~/components/notifications/ducks/notifications';
import { searchConversion } from '~/components/search/ducks/index';
import useTranslations from '~/components/translation/use-translations';
import { getSmallestSrcFromSrcSet } from '~/components/utilities/image-format';
import LazyLoadImage from '~/components/utilities/lazyload-image';
import useCasinoLocation from '~/hooks/use-casino-location';
import currencyFormatter from '~/libs/currency-formatter';
import { LICENSE_KSA } from '~/utils/license-utils';
import { isCasinoSearchPath } from '~/utils/pathname';

import ActivePlayers from './active-players';
import DropsWinsPragmatic from './drops-wins-pragmatic';
import {
	CardBottomContainer,
	CardHeaderContainer,
	CardInterface,
	GameCardContainer,
	GameClosedInfo,
	GameHover,
	GameHoverBottom,
	GameHoverTop,
	GameName,
	// ProviderName,
	PlayButton,
	PracticeButton,
	PracticeButtonContainer
} from './game-card-styles';
import GameTag from './game-tag';
import InfoButton from './info-button';
import GameJackpot from './jackpot';
import LiveFeedContainer from './live-feed-container';
import MediaCardWrapper from './media-card-wrapper';
import StakesPill from './stakes-pill';
import GameTagHot from './tag-hot';
import GameTagNew from './tag-new';

const CheckFunMode = ({ children, gameCode }: { children: JSX.Element; gameCode: string }) => {
	const game = useCasinoSharedSelector(state => gameDetailByGameCodeSelector(state, gameCode));
	const isFunModeAllowed = useIsFunModeAllowed(game);

	return isFunModeAllowed ? children : null;
};

const TEXT_KEYS = [
	'MOBILE_GAME_TAG_NEW',
	'MOBILE_GAME_TAG_HOT',
	'MOBILE_GAME_TAG_EXCLUSIVE_SWEDEN',
	'MOBILE_GAME_TAG_EXCLUSIVE_ALL',
	'PLAY',
	'PRACTICE_MODE',
	'LIVE_GAME_CLOSED'
];

type Props = {
	game: Omit<EnhancedGame, 'native' | 'index' | 'imageView'> & {
		imageView: {
			srcSet: string;
			formats: EnhancedAndFormattedImageView['formats'] | null;
		};
	};
	listName: string;
	index: number;
	trackGameClick?: (
		props: Omit<Parameters<typeof trackGameClickEvent>[0], 'listName' | 'isLiveCasinoLocation' | 'category'>
	) => void;
	disableHover?: boolean;
	radius?: string;
	disableBottom?: boolean;
	disableInfo?: boolean;
	disableTags?: boolean;
	forceGameData?: boolean;
	loading?: string;
	showJackpotTicker?: boolean;
	bodyScrollLockIgnore?: boolean;
	showLiveFeed?: boolean;
	forceStaticImage?: boolean;
};

const GameCard = ({
	game,
	game: {
		code,
		categoryCode,
		imageView: { srcSet, formats },
		otherView,
		isNew,
		isHot,
		jackpot,
		jackpotName = '',
		name,
		gameId,
		gameSiteId,
		tags
	},
	listName,
	index,
	trackGameClick = undefined,
	disableHover = false,
	radius = '',
	disableBottom = false,
	disableInfo = false,
	disableTags = false,
	forceGameData = false,
	loading = 'lazy',
	showJackpotTicker = true,
	bodyScrollLockIgnore = false,
	showLiveFeed = false,
	forceStaticImage
}: Props) => {
	const dispatch = useCasinoSharedDispatch();
	const [videoLoaded, setVideoLoaded] = useState(false);
	const cardRef = useRef<HTMLDivElement | null>(null);
	const cardBottomRef = useRef<HTMLDivElement | null>(null);
	const [cardWidth, setCardWidth] = useState<number>(0);
	const [cardHovered, setCardHovered] = useState(false);
	const [clickedPosition, setClickedPosition] = useState({ x: 0, y: 0 });
	const currency = useCasinoSharedSelector(selectCurrency);
	const locale = useCasinoSharedSelector(selectLocale);
	const jackpots = useCasinoSharedSelector(jackpotsSelector);
	const realTimeJackpotAmount = jackpots?.[jackpotName]?.amount ?? null;
	const { isCasinoLocation, isLiveCasinoLocation } = useCasinoLocation();

	const { gameLauncher } = useGameLauncher();
	const { toggleGameInfo } = useToggleGameInfo();

	const hasDepositLogin = useCasinoSharedSelector(hasDepositLoginSelector);
	const licenseName = useCasinoSharedSelector(selectLicense);
	const smaller = useCasinoSharedSelector(isSmallerSelector);
	const showDropsWinsPragmaticToggle = useCasinoSharedSelector(selectShowDropsWinsPragmatic);
	const enableLiveCasino3 = useCasinoSharedSelector(selectEnableLiveCasino3);

	const { translations: t, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	const gameLiveCasinoData = useLiveCasinoSelector(state => gameLiveCasinoDataSelector(state, game));
	const gameJackpotData = useCasinoSharedSelector(state => gameJackpotDataSelector(state, game));
	const { srcSet: inGameSrcSet, formats: inGameFormats } = useIngameImage(game, forceStaticImage);

	const preferredJackpotAmount = realTimeJackpotAmount || gameJackpotData || jackpot;
	const formattedJackpotAmount = currencyFormatter.format(preferredJackpotAmount, currency, locale, true) as string;
	// to cover both undefined and true checking false explicitly
	const isJackpotTickerVisible = formattedJackpotAmount && showJackpotTicker !== false;
	const isKSA = licenseName === LICENSE_KSA;

	// check and prevent play game when click and drag action is detected simultaneously
	// this action is using for click and drag/scroll horizontally game cards on desktop
	const isDragged = (e: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
		const { x, y } = clickedPosition;
		return Math.abs(e.clientX - x) > 20 || Math.abs(e.clientY - y) > 20;
	};

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		setClickedPosition({ x: e.clientX, y: e.clientY });
	};

	const setCardRef = useCallback((node: HTMLDivElement) => {
		if (node) {
			cardRef.current = node;
			setCardWidth(node.clientWidth);
		}
	}, []);

	useEffect(() => {
		const observer = new ResizeObserver(card => {
			setCardWidth(card[0].contentRect.width);
		});
		const node = cardRef.current;
		if (node) {
			observer.observe(node);
		}

		return () => {
			if (node) {
				observer.unobserve(node);
			}
		};
	}, []);

	const showDropsWinsPragmatic =
		showDropsWinsPragmaticToggle &&
		game.provider.code.match(/^(PRAGMATIC_PLAY|PRAGMATIC_PLAY_MOBILE)$/g) &&
		categoryCode !== 'CASINO_JACKPOT';

	const isLiveCasinoGame = categoryCode === 'CASINO_LIVE_CASINO';
	const liveCasinoGameType = getNormalizedGameType(gameLiveCasinoData?.type);
	const isHoverDisabled = (enableLiveCasino3 && liveCasinoGameType === 'blackjack' && showLiveFeed) || disableHover;

	const ImageComponent = useMemo(() => {
		if (!cardWidth) {
			return <div />;
		}

		return (
			<LazyLoadImage
				alt={name}
				src={getSmallestSrcFromSrcSet(inGameSrcSet || srcSet)}
				srcSet={inGameSrcSet || srcSet}
				pictureElementData={inGameFormats || formats}
				sizes={`${cardWidth}px`}
				loading={loading}
				enableImagePlaceholder
				placeholderProps={{ borderRadius: '12px' }}
			/>
		);
	}, [name, srcSet, formats, cardWidth, loading, inGameSrcSet, inGameFormats]);

	const videoComponent = useMemo(() => {
		if (!otherView || inGameSrcSet) return null;
		return (
			<LazyLoadImage
				alt={name}
				src={`${otherView?.path}/${otherView?.file}`}
				lazyload={loading === 'lazy'}
				onLoad={() => setVideoLoaded(true)}
			/>
		);
	}, [name, otherView, loading, inGameSrcSet]);

	// TODO: layeredMediaVisibility will be uncommented after BO and BE implementations are finished
	const layeredMediaVisibility = false;
	// const layeredMediaVisibility = useMemo(() => {
	// 	return enableLiveCasino3 && showLiveFeed && gameLiveCasinoData && !disableBottom && foregroundImage;
	// }, [enableLiveCasino3, showLiveFeed, gameLiveCasinoData, disableBottom]);

	const onToggleGameInfoClick = (e: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
		e.stopPropagation();
		insertTrackEvent({
			category: 'Game Card - Info Button',
			action: 'Clicked',
			label: `Game code: ${game.code} ${
				gameLiveCasinoData ? `Live Casino Game Type: ${gameLiveCasinoData.type}` : ''
			}`
		});
		toggleGameInfo(code, listName, index, forceGameData ? game : undefined);
	};

	const playGame = ({
		event,
		mode,
		selectedSeat,
		isTableFull
	}: {
		event: MouseEvent<HTMLElement, globalThis.MouseEvent>;
		mode: (typeof GAME_MODE)[keyof typeof GAME_MODE];
		selectedSeat?: number;
		isTableFull?: boolean;
	}) => {
		if (isDragged(event)) {
			return;
		}
		if (isKSA) {
			dispatch(startGamePlay());
		}
		if (trackGameClick) trackGameClick({ game, mode, position: index, seat: selectedSeat, isTableFull });
		if (mode === GAME_MODE.REAL && hasDepositLogin) {
			// Some franchises have two options to login
			// We know them if they not regularSiteType, check => selectIsRegularSiteType
			// DepositLogin and regular login
			// So we are opening game-info and let user can choose which one
			if (isCasinoLocation || isLiveCasinoLocation) {
				onToggleGameInfoClick(event);
			} else {
				dispatch(
					showNotification({
						messageKey: 'RESPONSIVE_LOGIN_TO_PLAY',
						color: 'neutral',
						autoHide: true
					})
				);
			}
		} else {
			if (isCasinoSearchPath(window.location.pathname)) {
				dispatch(searchConversion(code));
			}
			gameLauncher({
				event,
				mode,
				game,
				listName,
				listGameIndex: index,
				selectedSeat
			});
		}
	};

	const renderTag = () => {
		if (showDropsWinsPragmatic) {
			return <DropsWinsPragmatic />;
		}

		if (isLiveCasinoGame) {
			return;
		}

		if (tags && tags[0]) {
			const exclusiveTagText =
				licenseName === 'SGA' ? t.MOBILE_GAME_TAG_EXCLUSIVE_SWEDEN : t.MOBILE_GAME_TAG_EXCLUSIVE_ALL;

			return <GameTag name={tags[0].name} text={exclusiveTagText} />;
		}

		if (isNew) {
			return <GameTagNew text={t.MOBILE_GAME_TAG_NEW} />;
		}

		if (isHot) {
			return <GameTagHot text={t.MOBILE_GAME_TAG_HOT} />;
		}

		return null;
	};

	const onClickContainer = (event: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
		if (smaller || isHoverDisabled) {
			playGame({ event, mode: GAME_MODE.REAL });
		}
	};

	const onSeatsClick = (event: React.MouseEvent<HTMLElement>, seatIndex?: number) => {
		event.stopPropagation();
		insertTrackEvent({
			category: `Game Card - ${seatIndex ? 'Seat' : 'Full Table'}`,
			action: 'Clicked',
			label: `${seatIndex ? 'Seat' : 'Full Table'} click`
		});
		playGame({ event, mode: GAME_MODE.REAL, selectedSeat: seatIndex, isTableFull: seatIndex === undefined });
	};

	const isLiveGameClosedForBetting = enableLiveCasino3 && gameLiveCasinoData?.status?.isOnline === false;

	const handleMouseEnter = () => {
		if (isLiveGameClosedForBetting) {
			return;
		}
		setCardHovered(true);
	};

	const handleMouseLeave = () => {
		setCardHovered(false);
	};

	if (isLoadingTranslations) {
		return null;
	}

	return (
		<GameCardContainer
			role="presentation"
			onClick={onClickContainer}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseDown={handleMouseDown}
			disableHover={isHoverDisabled}
			showBlurredOverlay={isLiveGameClosedForBetting}
			radius={radius}
			ref={setCardRef}
		>
			<Card
				data-at="game-card"
				data-game-name={name}
				data-gamecode={code}
				data-gamesiteid={gameSiteId}
				data-gameid={gameId}
				alignItemsCenter
				biggerBorderRadius
			>
				<CardMedia
					data-at="game-card-image"
					src={getSmallestSrcFromSrcSet(srcSet)}
					srcSet={srcSet}
					lazyload
					imageComponent={ImageComponent}
					videoComponent={otherView ? videoComponent : null}
					showVideo={videoLoaded}
					layeredMediaVisibility={layeredMediaVisibility}
				/>

				{/* show a foreground image that is masked via css when new type of jackpot tickers are shown */}
				{isJackpotTickerVisible && (
					<MediaCardWrapper>
						<CardMedia
							data-at="game-card-foreground-image"
							src={getSmallestSrcFromSrcSet(srcSet)}
							srcSet={srcSet}
							lazyload
							imageComponent={ImageComponent}
							videoComponent={otherView ? videoComponent : null}
							showVideo={videoLoaded}
						/>
					</MediaCardWrapper>
				)}

				<CardInterface bodyScrollLockIgnore={bodyScrollLockIgnore}>
					{isLiveGameClosedForBetting && <GameClosedInfo>{t.LIVE_GAME_CLOSED}</GameClosedInfo>}
					<CardHeaderContainer>
						{!disableTags && (
							<>
								{renderTag()}

								{gameLiveCasinoData && !enableLiveCasino3 && (
									<ActivePlayers
										activePlayers={gameLiveCasinoData?.status?.activePlayers}
										totalSeats={gameLiveCasinoData?.status?.totalSeats}
										type={gameLiveCasinoData?.type}
									/>
								)}
							</>
						)}

						{!disableInfo && <InfoButton onClick={onToggleGameInfoClick} />}
					</CardHeaderContainer>

					{!disableBottom && (
						<CardBottomContainer id="card-bottom-container" ref={cardBottomRef}>
							{isJackpotTickerVisible && <GameJackpot text={formattedJackpotAmount} />}
							{gameLiveCasinoData &&
								(enableLiveCasino3 && showLiveFeed ? (
									<LiveFeedContainer
										activeSeats={gameLiveCasinoData?.status?.activeSeats}
										rouletteNumbers={gameLiveCasinoData?.status?.rouletteNumbers}
										radius={radius}
										clickHandler={onSeatsClick}
										liveCasinoGameType={liveCasinoGameType}
									/>
								) : (
									<StakesPill
										min={gameLiveCasinoData?.betLimits?.min}
										max={gameLiveCasinoData?.betLimits?.max}
										currency={currency}
									/>
								))}
						</CardBottomContainer>
					)}
				</CardInterface>

				{cardHovered && (
					<GameHover id="card-hover">
						<GameHoverTop id="card-hover-top">
							<GameName>{name}</GameName>
							{/* <ProviderName>{provider.name}</ProviderName> */}
						</GameHoverTop>

						<GameHoverBottom id="card-hover-bottom">
							<PlayButton onClick={e => playGame({ event: e, mode: GAME_MODE.REAL })}>
								{t.PLAY}
							</PlayButton>
							<CheckFunMode gameCode={code}>
								<PracticeButtonContainer>
									<PracticeButton onClick={e => playGame({ event: e, mode: GAME_MODE.FUN })}>
										{t.PRACTICE_MODE}
									</PracticeButton>
								</PracticeButtonContainer>
							</CheckFunMode>
						</GameHoverBottom>
					</GameHover>
				)}
			</Card>
		</GameCardContainer>
	);
};

export default GameCard;
